import React, { useEffect, useState, useReducer, useCallback } from "react";

import {
    Button,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    makeStyles,
    InputAdornment,
    TextField,
    CircularProgress
} from "@material-ui/core";

import {
    DeleteOutline,
    Edit,
    Search
} from "@material-ui/icons";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import TagModal from "../../components/TagModal";
import TagContactsModal from "../../components/TagContactsModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import { toast } from "react-toastify";

const useStyles = makeStyles(theme => ({
    mainPaper: {
        flex: 1,
        padding: theme.spacing(1),
        overflowY: "scroll",
        ...theme.scrollbarStyles,
    },
    customTableCell: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
    },
}));

const reducer = (state, action) => {
    if (action.type === "LOAD_TAGS") {
        const tags = action.payload;
        const newTags = [];

        tags.forEach(tag => {
            const tagIndex = state.findIndex(t => t.id === tag.id);
            if (tagIndex !== -1) {
                state[tagIndex] = tag;
            } else {
                newTags.push(tag);
            }
        });

        return [...state, ...newTags];
    }

    if (action.type === "UPDATE_TAGS") {
        const tag = action.payload;
        const tagIndex = state.findIndex(t => t.id === tag.id);

        if (tagIndex !== -1) {
            state[tagIndex] = tag;
            return [...state];
        } else {
            return [tag, ...state];
        }
    }

    if (action.type === "DELETE_TAG") {
        const tagId = action.payload;
        const tagIndex = state.findIndex(t => t.id === tagId);
        if (tagIndex !== -1) {
            state.splice(tagIndex, 1);
        }
        return [...state];
    }

    if (action.type === "RESET") {
        return [];
    }
};

const Tags = () => {
    const classes = useStyles();

    const [tags, dispatch] = useReducer(reducer, []);
    const [loading, setLoading] = useState(false);
    const [tagModalOpen, setTagModalOpen] = useState(false);
    const [selectedTag, setSelectedTag] = useState(null);
    const [searchParam, setSearchParam] = useState("");
    const [confirmModalOpen, setConfirmModalOpen] = useState(false);
    const [deletingTag, setDeletingTag] = useState(null);
    const [tagContactsModalOpen, setTagContactsModalOpen] = useState(false);

    const [selectedTagForContacts, setSelectedTagForContacts] = useState(null);

    useEffect(() => {
        dispatch({ type: "RESET" });
    }, [searchParam]);

    const fetchTags = useCallback(async () => {
        try {
            setLoading(true);
            const { data } = await api.get("/tags", {
                params: { searchParam }
            });
            dispatch({ type: "LOAD_TAGS", payload: data.tags });
            setLoading(false);
        } catch (err) {
            toastError(err);
            setLoading(false);
        }
    }, [searchParam]);

    useEffect(() => {
        fetchTags();
    }, [searchParam, fetchTags]);

    const handleOpenTagModal = () => {
        setTagModalOpen(true);
        setSelectedTag(null);
    };

    const handleCloseTagModal = () => {
        setTagModalOpen(false);
        setSelectedTag(null);
    };

    const handleOpenTagContactsModal = (tag) => {
        setSelectedTagForContacts(tag);
        setTagContactsModalOpen(true);
    };

    const handleCloseTagContactsModal = () => {
        setTagContactsModalOpen(false);
        setSelectedTagForContacts(null);
    };

    const handleEditTag = (tag) => {
        setSelectedTag(tag);
        setTagModalOpen(true);
    };

    const handleDeleteTag = async (tagId) => {
        try {
            await api.delete(`/tags/${tagId}`);
            toast.success(i18n.t("tags.toasts.deleted"));
        } catch (err) {
            toastError(err);
        }
        setDeletingTag(null);
        fetchTags();
    };

    return (
        <MainContainer>
            <TagModal
                open={tagModalOpen}
                onClose={handleCloseTagModal}
                tagId={selectedTag && selectedTag.id}
                reload={fetchTags}
            />
            <TagContactsModal
                open={tagContactsModalOpen}
                onClose={handleCloseTagContactsModal}
                tag={selectedTagForContacts}
                reloadTags={fetchTags}
            />
            <ConfirmationModal
                title={deletingTag && `${i18n.t("tags.toasts.deleted")} ${deletingTag.name}?`}
                open={confirmModalOpen}
                onClose={setConfirmModalOpen}
                onConfirm={() => handleDeleteTag(deletingTag.id)}
            >
                {i18n.t("tags.toasts.deleted")}
            </ConfirmationModal>
            <MainHeader>
                <Title>{i18n.t("tags.title")}</Title>
                <MainHeaderButtonsWrapper>
                    <TextField
                        placeholder={i18n.t("contacts.searchPlaceholder")}
                        type="search"
                        value={searchParam}
                        onChange={(e) => setSearchParam(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search color="secondary" />
                                </InputAdornment>
                            ),
                        }}
                    />
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleOpenTagModal}
                    >
                        {i18n.t("tags.buttons.add")}
                    </Button>
                </MainHeaderButtonsWrapper>
            </MainHeader>
            <Paper className={classes.mainPaper} variant="outlined">
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell align="center">ID</TableCell>
                                <TableCell align="center">{i18n.t("tags.table.name")}</TableCell>
                                <TableCell align="center">{i18n.t("tags.table.color")}</TableCell>
                                <TableCell align="center">{i18n.t("tags.table.contacts")}</TableCell>
                                <TableCell align="center">{i18n.t("tags.table.actions")}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            <>
                                {tags.map((tag) => (
                                    <TableRow key={tag.id}>
                                        <TableCell align="center">{tag.id}</TableCell>
                                        <TableCell align="center">
                                            <span
                                                style={{ cursor: "pointer", textDecoration: "underline", color: "blue" }}
                                                onClick={() => handleOpenTagContactsModal(tag)}
                                            >
                                                {tag.name}
                                            </span>
                                        </TableCell>
                                        <TableCell align="center">
                                            <div style={{ backgroundColor: tag.color, width: 24, height: 24, borderRadius: 12, margin: 'auto' }} />
                                        </TableCell>
                                        <TableCell align="center">{tag.contactsCount || 0}</TableCell>

                                        <TableCell align="center">
                                            <IconButton
                                                size="small"
                                                onClick={() => handleEditTag(tag)}
                                            >
                                                <Edit />
                                            </IconButton>

                                            <IconButton
                                                size="small"
                                                onClick={() => {
                                                    setDeletingTag(tag);
                                                    setConfirmModalOpen(true);
                                                }}
                                            >
                                                <DeleteOutline />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {loading && <TableRow><TableCell colSpan={3} align="center"><CircularProgress /></TableCell></TableRow>}
                            </>
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>
        </MainContainer>
    );
};

export default Tags;
